import clsx from "clsx";
import React, { startTransition, useCallback, useEffect } from "react";
import {
  Container,
  ContainerProps,
  Grid2,
  useMediaQuery,
  useTheme,
  IconButton,
  Stack,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";

import { useCurrentUser } from "../../hooks/useCurrentUser";
import { useMediaMobile } from "../../hooks/useMediaMobile";

import { ClientSummary } from "../coach-clients/ClientSummary";
import { ReactComponent as SettingIcon } from "../../icons/Setting.svg";
import { useNavigate } from "react-router-dom";
import { CLIENT_SETTING_ROUTE } from "../../routes/routes";
import TrackInfoTool from "../tools/TrackInfoTool";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import { isMobileApp } from "../../utils/mobile";
import { useTerraSdk } from "../../utils/terra-sdk";
import { useQuery } from "@tanstack/react-query";
import { CLIENT_TERRA_AUTH_TOKEN_QUERY_KEY } from "../settings/ClientSettingsProfile";
import TerraService from "../../services/TerraService";
import { useCurrentWorkspace } from "../../hooks/useCurrentWorkspace";
import { ProfileCards } from "../coach-clients/ProfileCards";

const useStyles = makeStyles((theme) => ({
  root: {
    maxWidth: theme.breakpoints.values.slg,
    marginTop: theme.spacing(3),
    position: "relative",
  },

  bodyweightCard: {
    cursor: "pointer",

    [theme.breakpoints.up("lg")]: {
      height: 224,
    },
  },
}));

export interface ClientProfileScreenProps
  extends Omit<ContainerProps, "children"> {}

export const ClientProfileActions = (
  phoneClickHandler,
  settingsClickHandler,
  isMobile,
) => {
  const theme = useTheme();
  return (
    <Stack direction={"row"}>
      {!isMobileApp && (
        <IconButton
          style={{
            borderRadius: theme.spacing(0.5),
          }}
          onClick={phoneClickHandler}
          size="large"
        >
          <PhoneIphoneOutlinedIcon sx={{ color: theme.palette.text.primary }} />
          {!isMobile && (
            <span
              style={{
                fontWeight: 500,
                fontSize: 18,
                lineHeight: "22px",
                color: theme.palette.text.primary,
                paddingLeft: theme.spacing(1.5),
              }}
            >
              Download app
            </span>
          )}
        </IconButton>
      )}
      <IconButton
        style={{
          borderRadius: theme.spacing(0.5),
        }}
        onClick={settingsClickHandler}
        size="large"
      >
        <SettingIcon />
        {!isMobile && (
          <span
            style={{
              fontWeight: 500,
              fontSize: 18,
              lineHeight: "22px",
              color: theme.palette.text.primary,
              paddingLeft: theme.spacing(1.5),
            }}
          >
            Settings
          </span>
        )}
      </IconButton>
    </Stack>
  );
};

export function ClientProfileScreen(props: ClientProfileScreenProps) {
  const navigate = useNavigate();
  const currentUser = useCurrentUser();
  const { nutritionsEnabled } = useCurrentWorkspace();
  const { className, ...other } = props;
  const username = currentUser?.username;
  const s = useStyles();
  const isMobile = useMediaMobile("md", false);
  const { breakpoints } = useTheme();
  const smUp = useMediaQuery(breakpoints.up("sm"));

  const { data: terraOptions } = useQuery({
    queryKey: [CLIENT_TERRA_AUTH_TOKEN_QUERY_KEY],
    queryFn: () => TerraService.generateAuthToken(),
  });

  const openSettings = React.useCallback(() => {
    navigate(CLIENT_SETTING_ROUTE);
  }, [navigate]);

  const { isImpersonating } = useCurrentUser();

  const {
    connected: terraConnected,
    init: initTerraSdk,
    sync: syncTerraSdk,
    error: terraError,
  } = useTerraSdk(terraOptions);

  useEffect(() => {
    if (terraError) {
      console.warn("Terra error: ", terraError);
    }
  }, [terraError]);

  const syncTerra = useCallback(async () => {
    if (isImpersonating) {
      return;
    }
    if (!terraConnected) {
      return;
    }
    await initTerraSdk();
    await syncTerraSdk();
  }, [isImpersonating, terraConnected, initTerraSdk, syncTerraSdk]);

  useEffect(() => {
    if (terraOptions) {
      syncTerra().catch((error) => {
        console.warn("Terra capacitor sync error", error);
      });
    }
  }, [terraOptions, syncTerra]);

  return (
    <>
      <Container className={clsx(s.root, className)} {...other}>
        <Grid2 container sx={{ mb: 3 }} spacing={3} alignItems="stretch">
          <Grid2
            size={{
              xs: 12,
              lg: 12,
            }}
          >
            <ClientSummary client={currentUser} />
          </Grid2>
          <ProfileCards client={currentUser} isPreview={false} />
        </Grid2>
      </Container>
      <TrackInfoTool
        trackInfo={{
          name: "Client - Profile",
        }}
      />
    </>
  );
}
